export type MonthlySpendReportAudience = 'all' | 'engineering';
export type MonthlySpendReportScope = MonthlySpendReportAudience | 'people';
export const AI_SPEND_OVERAGE_THRESHOLD_PERCENTAGE = 110;
/** Lowest threshold whose notification links to the budget-increase request form. */
export const AI_SPEND_BUDGET_REQUEST_THRESHOLD_PERCENTAGE = 90;

export type MonthlySpendLimit =
  | {
      type: 'finite';
      amountUsd: string;
      source: string;
    }
  | {
      type: 'unlimited';
      source: string;
    }
  | {
      type: 'unknown';
    };

export type SpendFreshness = {
  status: 'fresh' | 'stale' | 'unavailable';
  lastSuccessfulAt: string | null;
  error?: string;
};

/** Derived values inherit the oldest input and all failing input explanations. */
export const combineSpendFreshness = (
  values: Array<SpendFreshness | undefined>,
): SpendFreshness | undefined => {
  const known = values.filter(
    (value): value is SpendFreshness => value !== undefined,
  );
  if (!known.length) return undefined;
  const failed = known.filter(value => value.status !== 'fresh');
  const dates = known
    .map(value => value.lastSuccessfulAt)
    .filter((value): value is string => value !== null)
    .sort();
  let status: SpendFreshness['status'] = failed.length ? 'stale' : 'fresh';
  if (known.some(value => value.status === 'unavailable'))
    status = 'unavailable';
  return {
    status,
    lastSuccessfulAt: known.some(value => value.lastSuccessfulAt === null)
      ? null
      : (dates[0] ?? null),
    ...(failed.length
      ? {
          error: [
            ...new Set(failed.map(value => value.error).filter(Boolean)),
          ].join('; '),
        }
      : {}),
  };
};

export type MonthlySpendReport = {
  aggregation: 'total-across-limit-counting-providers';
  audience: MonthlySpendReportAudience;
  scope: MonthlySpendReportScope;
  selectedUserEmails: string[];
  availableUserEmails: string[];
  periodStart: string;
  periodEnd: string;
  observedAt: string;
  availableMonths: string[];
  populationSize: number;
  activePopulationSize: number;
  metrics: {
    freshness?: SpendFreshness;
    totalUsd: string;
    averageUsd: string | null;
    medianUsd: string | null;
    p90Usd: string | null;
  };
  topSpenders: Array<{
    rank: number;
    email: string;
    totalUsd: string;
    freshness?: SpendFreshness;
    limitFreshness?: SpendFreshness;
    limit?: MonthlySpendLimit;
    utilizationPercentage?: number | null;
    overage?: boolean;
    spendByProvider: Array<{
      provider: string;
      amountUsd: string;
      countsTowardLimit: boolean;
      freshness?: SpendFreshness;
      spendByModelFamily?: Array<{
        modelFamily: string;
        amountUsd: string;
        percentage: number;
      }>;
    }>;
  }>;
};

/** Who an OpenAI Platform API key's spend is attributed to. */
export type OpenAIPlatformKeyOwner =
  | { type: 'user'; email: string }
  | { type: 'service_account'; name: string }
  | { type: 'unknown'; apiKeyId: string };

/**
 * Informational spend of the whole OpenAI organization's API keys for one
 * month. It is not part of the monthly spend report and does not count toward
 * limits. All USD values are decimal strings.
 */
export type OpenAIPlatformKeySpend = {
  month: string;
  /** When this result was read from OpenAI (ISO 8601, UTC). */
  fetchedAt: string;
  /**
   * Set when the latest refresh failed and this is the last good result, so
   * `fetchedAt` is older than the cache lifetime.
   */
  stale?: boolean;
  /** Start date (YYYY-MM-DD, UTC) of the latest billed day, if any. */
  settledThrough: string | null;
  totalUsd: string;
  owners: Array<{
    owner: OpenAIPlatformKeyOwner;
    amountUsd: string;
    keys: Array<{
      apiKeyId: string;
      name: string | null;
      projectName: string | null;
      amountUsd: string;
    }>;
  }>;
};
